import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, LineChart, X, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminHydrateBenchmarkEtfsButton — Round P108au (Feb 2026).
 *
 * One-click seeder for the ~40 canonical benchmark ETFs used by the
 * Insights-tab Benchmark Performance card's dynamic family classifier
 * (`_classify_benchmark_family` in server.py).
 *
 * The classifier only fires when a ticker is flagged as a fund vehicle
 * in `stock_cache` (asset_type=ETF, is_etf=True, or has fund_family).
 * Without this seed, a cold-cache production install would default
 * every international / EM / bond / FX ETF to SPY/SCHD/VYM until each
 * ticker was individually opened in the Stock Modal. This button
 * warms all of them from Massive in one shot.
 *
 * Idempotent — tickers already in stock_cache with a description are
 * skipped. Runtime ≤ 30 sec for the full 40-ticker set.
 *
 * Rendered only for `user.role === 'admin'`.
 */
export default function AdminHydrateBenchmarkEtfsButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [force, setForce] = useState(false);

  if (user?.role !== 'admin') return null;

  const runHydration = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/hydrate-benchmark-etfs`,
        { force },
        { withCredentials: true, timeout: 90 * 1000 },
      );
      setLastResult(data);
      const nHydrated = data?.hydrated?.length || 0;
      const nSkipped = data?.skipped?.length || 0;
      const nNotFound = data?.not_found?.length || 0;
      toast.success(
        `Benchmark seed complete — ${nHydrated} hydrated, ${nSkipped} already cached`,
        {
          description: nNotFound > 0
            ? `Massive didn't recognize ${nNotFound} ticker${nNotFound === 1 ? '' : 's'} (usually liquidated funds)`
            : 'All requested tickers processed',
        },
      );
    } catch (err) {
      toast.error('Benchmark seed failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-hydrate-benchmark-etfs-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-900 dark:text-fuchsia-200 border border-fuchsia-500/40 transition-colors"
        title="Seed the ~40 canonical benchmark ETFs (int'l, EM, bond, REIT, FX) into stock_cache so the Benchmark Performance card's family classifier fires on a cold cache"
      >
        <LineChart className="w-4 h-4" />
        Seed benchmark ETFs
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setOpen(false)}
          data-testid="admin-hydrate-benchmark-etfs-modal"
        >
          <div
            className="relative w-full max-w-lg bg-card border border-fuchsia-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">Seed Benchmark ETFs</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  One-click seed after a fresh deploy — populates cache metadata for the ~40 canonical benchmark ETFs so the Benchmark Performance card&apos;s family classifier fires on a cold cache.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="admin-hydrate-benchmark-etfs-close"
                className="p-1 rounded hover:bg-muted/40"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Hits <code className="text-foreground bg-muted/30 px-1 rounded">Massive ticker-details</code> for every ETF in the canonical benchmark set — dividend trio (SPY/SCHD/VYM), growth trio (SPYG/QQQ/IWF), developed markets (EFA/IEFA/SPDW/DBEF/HEFA/VSS), emerging (IEMG/VWO/EMXC/XCEM/DBEM/HEEM), regional (EPP/VNQI), bonds (BNDX/IAGG/BWX/ISHG/EMB/EMLC), FX (UUP/UDN/ELD/CEW/USDU + FXA/FXB/FXC/FXE/FXF/FXY) — and upserts asset_type / is_etf / name / description into <code className="text-foreground bg-muted/30 px-1 rounded">stock_cache</code>.
              </p>

              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  disabled={busy}
                  data-testid="admin-hydrate-benchmark-etfs-force"
                  className="w-3.5 h-3.5"
                />
                <span className="text-muted-foreground">
                  Force refresh even for tickers already cached with a description
                </span>
              </label>

              <button
                onClick={runHydration}
                disabled={busy}
                data-testid="admin-hydrate-benchmark-etfs-start"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 border border-fuchsia-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LineChart className="w-4 h-4" />}
                {busy ? 'Seeding…' : 'Run seed now'}
              </button>

              {lastResult && (
                <div
                  className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-2"
                  data-testid="admin-hydrate-benchmark-etfs-result"
                >
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-300">
                      Last run
                    </span>
                  </div>
                  <div className="text-xs font-mono space-y-0.5">
                    <div>
                      Requested:{' '}
                      <span className="text-foreground font-bold">{lastResult.requested}</span>
                    </div>
                    <div>
                      Hydrated:{' '}
                      <span className="text-emerald-500 font-bold">
                        {lastResult.hydrated?.length || 0}
                      </span>
                      {lastResult.hydrated?.length > 0 && (
                        <span className="ml-2 text-[10px] text-muted-foreground">
                          {lastResult.hydrated.slice(0, 12).join(', ')}
                          {lastResult.hydrated.length > 12 ? '…' : ''}
                        </span>
                      )}
                    </div>
                    <div>
                      Skipped (already cached):{' '}
                      <span className="text-sky-500 font-bold">
                        {lastResult.skipped?.length || 0}
                      </span>
                    </div>
                    <div>
                      Not in Massive:{' '}
                      <span
                        className={
                          lastResult.not_found?.length > 0
                            ? 'text-amber-500 font-bold'
                            : 'text-muted-foreground'
                        }
                      >
                        {lastResult.not_found?.length || 0}
                      </span>
                      {lastResult.not_found?.length > 0 && (
                        <span className="ml-2 text-[10px] text-muted-foreground">
                          {lastResult.not_found.join(', ')}
                        </span>
                      )}
                    </div>
                    {lastResult.errors?.length > 0 && (
                      <div className="text-rose-500 font-bold">
                        Errors: {lastResult.errors.length}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
